import ProficienciesPage from "./pages/ProficienciesPage";
import FeedPage from "./pages/FeedPage";
import DashboardPage from "./pages/DashboardPage";
import PilotPage from "./pages/PilotPage";
import PilotsPage from "./pages/PilotsPage";
import AchievementsPage from "./pages/AchievementsPage";
import AdminPage from "./pages/AdminPage";
import AdminSignInPage from "./pages/AdminSignInPage";
import AdminMetadataPage from "./pages/AdminMetadataPage";
import AdminPilotStatePage from "./pages/AdminPilotStatePage";
import GreenieBoard from "./pages/GreenieBoard";
import StatsRoutePage from "./pages/StatsRoutePage";
import HighScoresPage from "./pages/HighScoresPage";
import { activeLegacyRedirect } from "./lens/legacyRoutes";
import { useEffect, useRef, useState } from "react";

type AppRoute = "dashboard" | "feed" | "pilots" | "achievements" | "greenie_board" | "admin";
type AdminRoute = "admin" | "admin_sign_in" | "admin_metadata" | "admin_pilot_state";

type RouteState =
  | { route: "dashboard" }
  | { route: "stats" }
  | { route: "proficiencies"; airframe: string | null }
  | { route: "feed" }
  | { route: "pilots"; pilotUid: string | null }
  | { route: "achievements" }
  | { route: "greenie_board" }
  | { route: "high_scores" }
  | { route: AdminRoute };

const ADMIN_TOKEN_KEY = "checkride.admin.jwt";
const ADMIN_RETURN_HASH_KEY = "checkride.admin.return_hash";

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const normalized = token.trim().startsWith("Bearer ") ? token.trim().slice(7) : token.trim();
  const parts = normalized.split(".");
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (typeof exp !== "number") return false;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp <= nowSeconds;
}

function resolveRouteFromHash(hash: string): RouteState {
  const normalized = (hash || "").replace(/^#/, "");
  const [pathPart, queryString = ""] = normalized.split("?");
  const parts = pathPart.split("/").filter(Boolean);
  const query = new URLSearchParams(queryString);

  if (parts[0] === "pilots") {
    const raw = parts[1] ?? "";
    return { route: "pilots", pilotUid: raw ? safeDecodeURIComponent(raw) : null };
  }

  if (parts[0] === "stats") return { route: "stats" };

  if (parts[0] === "dashboard") {
    return { route: "dashboard" };
  }

  if (parts[0] === "proficiencies") return { route: "proficiencies", airframe: query.get("airframe") };

  if (parts[0] === "feed") {
    return { route: "feed" };
  }

  if (parts[0] === "achievements") {
    return { route: "achievements" };
  }

  if (parts[0] === "greenie_board") {
    return { route: "greenie_board" };
  }

  if (parts[0] === "high_scores") return { route: "high_scores" };

  if (parts[0] === "admin") {
    if (parts[1] === "sign_in") {
      return { route: "admin_sign_in" };
    }
    if (parts[1] === "metadata") {
      return { route: "admin_metadata" };
    }
    if (parts[1] === "pilot_state") {
      return { route: "admin_pilot_state" };
    }
    return { route: "admin" };
  }

  return { route: "dashboard" };
}

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function currentAnalyticsPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function trackCurrentPageView() {
  const path = currentAnalyticsPath();
  // Umami v2 (cloud.umami.is/script.js) only auto-tracks History API navigation.
  // This app is hash-routed, so route changes must be reported manually with an
  // explicit url that includes the hash.
  window.umami?.track((props) => ({ ...props, url: path }));
}

const App = () => {
  const [route, setRoute] = useState<RouteState>(() => resolveRouteFromHash(window.location.hash));
  const hasTrackedInitialRouteRef = useRef(false);

  useEffect(() => {
    const onHashChange = () => {
      const legacyTarget = activeLegacyRedirect(window.location.hash);
      if (legacyTarget) {
        window.location.replace(legacyTarget);
        return;
      }
      const next = resolveRouteFromHash(window.location.hash);
      setRoute((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    window.addEventListener("hashchange", onHashChange);
    // Translations load before the first render, so a hash change in that window is missed.
    onHashChange();
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (!route.route.startsWith("admin")) return;
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    const tokenExpired = token ? isTokenExpired(token) : false;

    if (route.route !== "admin_sign_in" && route.route.startsWith("admin") && (!token || tokenExpired)) {
      if (tokenExpired) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
      }
      const currentHash = window.location.hash || "#/admin";
      sessionStorage.setItem(ADMIN_RETURN_HASH_KEY, currentHash);
      if (currentHash !== "#/admin/sign_in") {
        window.location.hash = "#/admin/sign_in";
      }
      return;
    }

    if (route.route === "admin_sign_in" && token) {
      const returnHash = sessionStorage.getItem(ADMIN_RETURN_HASH_KEY) || "#/admin";
      sessionStorage.removeItem(ADMIN_RETURN_HASH_KEY);
      if (returnHash !== "#/admin/sign_in" && returnHash !== window.location.hash) {
        window.location.hash = returnHash;
      }
    }
  }, [route]);

  useEffect(() => {
    if (!hasTrackedInitialRouteRef.current) {
      hasTrackedInitialRouteRef.current = true;
      return;
    }

    trackCurrentPageView();
  }, [route]);

  if (route.route === "stats") return <StatsRoutePage />;

  if (route.route === "high_scores") return <HighScoresPage />;

  if (route.route === "pilots" && route.pilotUid) {
    return <PilotPage pilotUid={route.pilotUid} />;
  }

  if (route.route === "pilots") {
    return <PilotsPage />;
  }

  if (route.route === "proficiencies") return <ProficienciesPage airframe={route.airframe} />;

  if (route.route === "achievements") {
    return <AchievementsPage />;
  }

  if (route.route === "greenie_board") {
    return <GreenieBoard />;
  }

  if (route.route === "admin") {
    return <AdminPage />;
  }

  if (route.route === "admin_sign_in") {
    return <AdminSignInPage />;
  }

  if (route.route === "admin_metadata") {
    return <AdminMetadataPage />;
  }

  if (route.route === "admin_pilot_state") {
    return <AdminPilotStatePage />;
  }

  if (route.route === "dashboard") {
    return <DashboardPage />;
  }

  return <FeedPage />;
};

export default App;

